import { Component, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Popover } from 'primeng/popover';
import {
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import {
  AccountType,
  Microsoft365GroupSearchResult,
  MicrosoftTeamsCallQueueView,
  MicrosoftTeamsCallQueueViewEntityStatus,
  MicrosoftTeamsUserResult,
  UserPhoto,
  VoiceAppViewEntity,
  VoiceAppViewEntityType,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes, ZiroButtonComponent } from '../../../../button/button.component';
import {
  DatatableColumn,
  DatatableRow,
  DatatableRowActionButton,
} from '../../../../shared/tables/datatable/datatable.models';
import { DatatableComponent } from '../../../../shared/tables/datatable/datatable.component';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsUpdate } from '../../../../forms/ziro-forms-models';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import { ZpmUserPhotoContext } from '../../../../shared/contexts/zpm-user-photo.context';
import {
  MicrosoftTeamsUserSearchResource,
  TeamsUserSearchScope,
} from '../../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { TeamsCallQueue365GroupResource } from '../../../teams-call-queue-365-group.resource';
import { TeamsCallQueueTeamsResource } from '../../../teams-call-queue-teams.resource';
import { TeamsCallQueueSchedulingGroupsResource } from '../../../teams-call-queue-scheduling-groups.resource';
import { TeamsCallQueueChannelsResource } from '../../../teams-call-queue-channels.resource';

export enum TeamsCallQueueType {
  USER = 'User',
  GROUP = 'Group',
  RESOURCE_ACCOUNT = 'Resource Account',
}

export enum AgentMode {
  USERS_AND_GROUPS = 'USERS_AND_GROUPS',
  TEAMS_CHANNEL = 'TEAMS_CHANNEL',
  SHIFTS = 'SHIFTS',
}

export interface UiAgentSettings {
  teamId: string;
  teamSchedulingGroupId: string;
  microsoftTeamsChannelId: string;
}

export interface UiAgentSelection {
  mode: AgentMode;
  userGuids: string[];
  groupGuids: string[];
  microsoftTeamsTeamsId: string;
  teamSchedulingGroupId: string;
  microsoftTeamsChannelId: string;
}

interface AgentDatatableRow extends DatatableRow {
  type: string;
  name: string;
  upn: string;
  guid: string;
}

interface UiCallQueueUser extends VoiceAppViewEntity {
  photoBase64: string;
}

interface UiTeamsUser extends MicrosoftTeamsUserResult {
  photoBase64: string;
}

@Component({
  selector: 'ziro-agents-card',
  imports: [
    CommonModule,
    ZiroFormsModule,
    TranslateModule,
    PipesModule,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbTypeahead,
    Popover,
    ZiroButtonComponent,
    DatatableComponent,
  ],
  templateUrl: './agents-card.component.html',
})
export class AgentsCardComponent extends ZiroFormAbstractDirective<UiAgentSettings> implements OnInit, OnDestroy {
  private static HEADER_SUBTEXT_BY_MODE: Record<AgentMode, string> = {
    [AgentMode.USERS_AND_GROUPS]: 'tkey;teams_call_queue.agents.mode.users_and_groups.subtext',
    [AgentMode.TEAMS_CHANNEL]: 'tkey;teams_call_queue.agents.mode.teams_channel.subtext',
    [AgentMode.SHIFTS]: 'tkey;teams_call_queue.agents.mode.shifts.subtext',
  };

  private _translateService = inject(TranslateService);
  private _photoContext = inject(ZpmUserPhotoContext);
  private _microsoftTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _msCallQueue365GroupResource = inject(TeamsCallQueue365GroupResource);
  private _teamsResource = inject(TeamsCallQueueTeamsResource);
  private _schedulingGroupsResource = inject(TeamsCallQueueSchedulingGroupsResource);
  private _channelsResource = inject(TeamsCallQueueChannelsResource);

  callQueueViewSource = input<MicrosoftTeamsCallQueueView>();

  agentSelectionChanged = output<UiAgentSelection>();

  agentPopover = viewChild<Popover>('agentPopover');

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  agentModes = AgentMode;
  activeAgentMode = AgentMode.USERS_AND_GROUPS;
  isSearching = false;
  formConfig: ZiroFormConfig;
  agentRows = signal<AgentDatatableRow[]>([]);
  agentColumns: DatatableColumn<AgentDatatableRow>[] = [
    {
      name: 'type',
      label: 'tkey;teams_call_queue.resource_accounts.agent_table.column.type',
    },
    {
      name: 'name',
      label: 'tkey;teams_call_queue.resource_accounts.agent_table.column.name',
    },
    {
      name: 'upn',
      label: 'tkey;teams_call_queue.resource_accounts.agent_table.column.upn',
    },
  ];

  private _viewGroups: VoiceAppViewEntity[] = [];
  private _rowUsers: UiCallQueueUser[] = [];
  private _userGuids: string[] = [];
  private _groupGuids: string[] = [];
  private _teamsUsers: MicrosoftTeamsUserResult[];
  private _ms365Groups: Microsoft365GroupSearchResult[];
  private _agentPhotoSub: Observable<number>;
  private _isTeamOptionsRequested = false;
  private _knownTeamOption: ZiroSelectOption;
  private _knownChannelOption: ZiroSelectOption;
  private _knownSchedulingGroupOption: ZiroSelectOption;
  private _isLoadedChannelConfigurationUnresolved = false;
  private _isLoadingTeamOptions = false;
  private _isLoadingChannelOptions = false;
  private _isLoadingSchedulingGroupOptions = false;
  private _selectedTeamId = '';
  private _channelOptionsTeamRequests = new Subject<string>();
  private _schedulingGroupOptionsTeamRequests = new Subject<string>();
  private _dependentOptionsResetRequests = new Subject<void>();
  private _subs = new Subscription();

  ngOnInit(): void {
    const { users, groups, tenantId, microsoftTeamsChannel, microsoftTeamsTeams, teamSchedulingGroup } =
      this.callQueueViewSource();

    if (microsoftTeamsChannel) {
      this.activeAgentMode = AgentMode.TEAMS_CHANNEL;
    } else if (teamSchedulingGroup) {
      this.activeAgentMode = AgentMode.SHIFTS;
    }

    const isChannelMode = this.activeAgentMode === AgentMode.TEAMS_CHANNEL;

    this._viewGroups = isChannelMode ? [] : groups;
    this._groupGuids = isChannelMode ? [] : groups.map((group) => group.guid);
    this._rowUsers = users.map((user) => ({ ...user, photoBase64: '' }));
    this._userGuids = users.map((user) => user.guid);

    const isSingleGroupChannelTeam = isChannelMode && groups.length === 1;
    let knownTeamDisplayName = '';

    if (isSingleGroupChannelTeam) {
      this._selectedTeamId = groups[0].guid;
      knownTeamDisplayName = groups[0].displayName;
    } else if (this.activeAgentMode === AgentMode.SHIFTS && microsoftTeamsTeams) {
      this._selectedTeamId = microsoftTeamsTeams.id;
      knownTeamDisplayName = microsoftTeamsTeams.displayName;
    }

    this._isLoadedChannelConfigurationUnresolved =
      isChannelMode &&
      (microsoftTeamsChannel.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND ||
        !this._selectedTeamId);

    const deletedOptionLabel: string = this._translateService.instant(
      'tkey;teams_call_queue.agents.option.not_found.label',
    );
    this._knownTeamOption = this._selectedTeamId
      ? { label: knownTeamDisplayName || deletedOptionLabel, value: this._selectedTeamId }
      : null;
    this._knownChannelOption = microsoftTeamsChannel
      ? { label: microsoftTeamsChannel.displayName || deletedOptionLabel, value: microsoftTeamsChannel.id }
      : null;
    this._knownSchedulingGroupOption = teamSchedulingGroup
      ? { label: teamSchedulingGroup.displayName || deletedOptionLabel, value: teamSchedulingGroup.id }
      : null;

    const knownTeamOptions: ZiroSelectOption[] = this._knownTeamOption ? [this._knownTeamOption] : [];
    const knownChannelOptions: ZiroSelectOption[] = this._knownChannelOption ? [this._knownChannelOption] : [];
    const knownSchedulingGroupOptions: ZiroSelectOption[] = this._knownSchedulingGroupOption
      ? [this._knownSchedulingGroupOption]
      : [];

    this.formConfig = {
      fields: {
        teamId: {
          dataAutomation: 'agents-team',
          label: 'tkey;teams_call_queue.agents.team.label',
          componentConfig: new ZiroSelectConfig({
            options: knownTeamOptions,
            placeholder: 'tkey;teams_call_queue.agents.team.placeholder',
            bindValue: 'value',
            triggerLoading: () => this.isTeamOptionsLoading(),
          }),
          required: () => this.activeAgentMode !== AgentMode.USERS_AND_GROUPS,
          hidden: () => this.activeAgentMode === AgentMode.USERS_AND_GROUPS,
          valExcluded: () => this.activeAgentMode === AgentMode.USERS_AND_GROUPS,
        },
        microsoftTeamsChannelId: {
          dataAutomation: 'agents-channel',
          label: 'tkey;teams_call_queue.agents.channel.label',
          componentConfig: new ZiroSelectConfig({
            options: knownChannelOptions,
            placeholder: 'tkey;teams_call_queue.agents.channel.placeholder',
            bindValue: 'value',
            triggerLoading: () => this.isChannelOptionsLoading(),
          }),
          required: () => this.activeAgentMode === AgentMode.TEAMS_CHANNEL,
          hidden: () => this.activeAgentMode !== AgentMode.TEAMS_CHANNEL,
          valExcluded: () => this.activeAgentMode !== AgentMode.TEAMS_CHANNEL,
          disabled: () => !this._selectedTeamId,
          disabledTooltip: 'tkey;teams_call_queue.agents.team_required.disabled.tooltip',
        },
        teamSchedulingGroupId: {
          dataAutomation: 'agents-scheduling-group',
          label: 'tkey;teams_call_queue.agents.scheduling_group.label',
          componentConfig: new ZiroSelectConfig({
            options: knownSchedulingGroupOptions,
            placeholder: 'tkey;teams_call_queue.agents.scheduling_group.placeholder',
            bindValue: 'value',
            triggerLoading: () => this.isSchedulingGroupOptionsLoading(),
          }),
          required: () => this.activeAgentMode === AgentMode.SHIFTS,
          hidden: () => this.activeAgentMode !== AgentMode.SHIFTS,
          valExcluded: () => this.activeAgentMode !== AgentMode.SHIFTS,
          disabled: () => !this._selectedTeamId,
          disabledTooltip: 'tkey;teams_call_queue.agents.team_required.disabled.tooltip',
        },
      },
      options: {
        columnClasses: {
          label: 'col-12',
          input: 'col-12',
        },
      },
    };

    this.entitySource.next({
      teamId: this._selectedTeamId,
      teamSchedulingGroupId: teamSchedulingGroup ? teamSchedulingGroup.id : '',
      microsoftTeamsChannelId: microsoftTeamsChannel ? microsoftTeamsChannel.id : '',
    });

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<UiAgentSettings>) => {
      if (update.new.teamId !== this._selectedTeamId) {
        this._selectedTeamId = update.new.teamId;
        this._onSelectedTeamChanged();
      }

      const previousChannelId = update.old ? update.old.microsoftTeamsChannelId : update.new.microsoftTeamsChannelId;
      const hasPickedNewChannel =
        update.new.microsoftTeamsChannelId !== previousChannelId &&
        !!update.new.teamId &&
        !!update.new.microsoftTeamsChannelId;

      if (hasPickedNewChannel) {
        this._isLoadedChannelConfigurationUnresolved = false;
      }

      this._emitAgentSelectionForActiveMode();
    });

    this._subs.add(formUpdateSub);

    const channelOptionsSub = this._channelOptionsTeamRequests
      .pipe(switchMap((teamId: string) => this._channelsResource.get(tenantId.toString(), teamId)))
      .subscribe((fieldConfig) => {
        this._isLoadingChannelOptions = false;
        this.fieldChannels?.microsoftTeamsChannelId?.componentConfigSource.next(
          new ZiroSelectConfig({
            options: this._withKnownOption(
              fieldConfig.possibleValues.map((channel) => ({
                label: channel.displayName,
                value: channel.id,
              })),
              this._knownChannelOption,
              this.formData.microsoftTeamsChannelId,
            ),
            placeholder: 'tkey;teams_call_queue.agents.channel.placeholder',
            bindValue: 'value',
          }),
        );
      });

    this._subs.add(channelOptionsSub);

    const schedulingGroupOptionsSub = this._schedulingGroupOptionsTeamRequests
      .pipe(switchMap((teamId: string) => this._schedulingGroupsResource.get(tenantId.toString(), teamId)))
      .subscribe((fieldConfig) => {
        this._isLoadingSchedulingGroupOptions = false;
        this.fieldChannels?.teamSchedulingGroupId?.componentConfigSource.next(
          new ZiroSelectConfig({
            options: this._withKnownOption(
              fieldConfig.possibleValues.map((schedulingGroup) => ({
                label: schedulingGroup.displayName,
                value: schedulingGroup.id,
              })),
              this._knownSchedulingGroupOption,
              this.formData.teamSchedulingGroupId,
            ),
            placeholder: 'tkey;teams_call_queue.agents.scheduling_group.placeholder',
            bindValue: 'value',
          }),
        );
      });

    this._subs.add(schedulingGroupOptionsSub);

    const dependentOptionsResetSub = this._dependentOptionsResetRequests.pipe(delay(0)).subscribe(() => {
      this.fieldChannels?.microsoftTeamsChannelId?.componentConfigSource.next(
        new ZiroSelectConfig({
          options: [],
          placeholder: 'tkey;teams_call_queue.agents.channel.placeholder',
          bindValue: 'value',
        }),
      );
      this.fieldChannels?.teamSchedulingGroupId?.componentConfigSource.next(
        new ZiroSelectConfig({
          options: [],
          placeholder: 'tkey;teams_call_queue.agents.scheduling_group.placeholder',
          bindValue: 'value',
        }),
      );
    });

    this._subs.add(dependentOptionsResetSub);

    this._photoContext
      .initCallQueueUserPhotos(
        tenantId.toString(),
        users.map((user) => user.upn),
      )
      .subscribe();

    const photoSub = this._photoContext.state$.pipe(take(1)).subscribe(() => {
      const userPhotos: UserPhoto[] = this._photoContext.getUserPhotos();
      this._rowUsers = this._rowUsers.map((user) => ({
        ...user,
        photoBase64: userPhotos.find((photo) => photo.userPrincipalName === user.upn)?.photoBase64 || '',
      }));
      this._buildTableRows();
    });

    this._subs.add(photoSub);

    this._buildTableRows();

    if (this.activeAgentMode !== AgentMode.USERS_AND_GROUPS) {
      this._loadTeamOptions();
      this._loadDependentOptions();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  setAgentMode(mode: AgentMode): void {
    this.activeAgentMode = mode;
    this._setFieldStates();

    if (mode !== AgentMode.USERS_AND_GROUPS) {
      this._loadTeamOptions();
      this._loadDependentOptions();
    }

    this._emitAgentSelectionForActiveMode();
  }

  getHeaderSubtext(): string {
    return AgentsCardComponent.HEADER_SUBTEXT_BY_MODE[this.activeAgentMode];
  }

  isTeamOptionsLoading(): boolean {
    return this._isLoadingTeamOptions;
  }

  isChannelOptionsLoading(): boolean {
    return this._isLoadingChannelOptions;
  }

  isSchedulingGroupOptionsLoading(): boolean {
    return this._isLoadingSchedulingGroupOptions;
  }

  agentSearch: (
    text$: Observable<string>,
  ) => Observable<string[] | readonly (MicrosoftTeamsUserResult | Microsoft365GroupSearchResult)[]> = (
    text$: Observable<string>,
  ) =>
    text$.pipe(
      tap((term) => {
        if (term && term.length >= 3) {
          this.isSearching = true;
        }
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 3) {
          return of([] as Microsoft365GroupSearchResult[]);
        }
        return combineLatest([
          this._msCallQueue365GroupResource.search(this.callQueueViewSource().tenantId.toString(), term),
          this._microsoftTeamsUserSearchResource.search(
            this.callQueueViewSource().tenantId.toString(),
            TeamsUserSearchScope.CALL_QUEUE_MANAGEMENT,
            term.trim(),
            null,
            40,
            AccountType.USER,
          ),
        ]);
      }),
      switchMap(([ms365Groups, teamsUsers]: [Microsoft365GroupSearchResult[], MicrosoftTeamsUserResult[]]) => {
        if (!ms365Groups || !teamsUsers) {
          return [] as string[];
        }
        this._ms365Groups = ms365Groups;
        this._teamsUsers = teamsUsers;

        return this._photoContext.initCallQueueUserPhotos(
          this.callQueueViewSource().tenantId.toString(),
          this._teamsUsers.map((user) => user.userPrincipalName),
        );
      }),
      switchMap(() => this._photoContext.state$),
      map(() => {
        const userPhotos: UserPhoto[] = this._photoContext.getUserPhotos();
        this._teamsUsers = this._teamsUsers
          .map((user) => ({
            ...user,
            photoBase64:
              userPhotos.find((photo) => photo.userPrincipalName === user.userPrincipalName)?.photoBase64 || '',
          }))
          .map((user) => ({
            ...user,
            markup: this.buildDropdownGraphicMarkup(user),
          }));

        this._ms365Groups = this._ms365Groups.map((group) => ({
          ...group,
          markup: `
          <div>
            <span class="fa-stack left-3">
              <i class="${ZiroIcons.CIRCLE} fa-stack-2x"></i>
              <i class="${ZiroIcons.GROUP} text-white fa-stack-1x"></i>
            </span>
            <span>${group.name}</span>
          </div>
        `,
        }));

        return [...this._ms365Groups, ...this._teamsUsers];
      }),

      tap(() => (this.isSearching = false)),
      catchError(() => of([] as (MicrosoftTeamsUserResult | Microsoft365GroupSearchResult)[])),
    );

  formatter = (result: Microsoft365GroupSearchResult | MicrosoftTeamsUserResult): string => {
    if ('userPrincipalName' in result) {
      return result.ref.displayName;
    } else {
      return result.name;
    }
  };

  buildDropdownGraphicMarkup(user: UiTeamsUser): string {
    if (user.photoBase64) {
      return `
          <span class="d-flex align-items-center">
            <span class="datatable-row-graphic me-2">
              <img src="data:image/png;base64,${user.photoBase64}" alt="${user.ref.displayName}">
            </span>
            <span>${user.ref.displayName} | ${user.userPrincipalName}</span>
          </span>
        `;
    } else {
      return `
            <span class="d-flex align-items-center">
                <i class="${ZiroIcons.CIRCLE_USER} font-size-32 me-2"></i>
              <span>${user.ref.displayName} | ${user.userPrincipalName}</span>
            </span>
          `;
    }
  }

  addAgent(selectedAgent: MicrosoftTeamsUserResult | Microsoft365GroupSearchResult): void {
    let targetAgent: MicrosoftTeamsUserResult | Microsoft365GroupSearchResult;

    if ('userPrincipalName' in selectedAgent) {
      targetAgent = this._teamsUsers.find((agent) => agent.userPrincipalName === selectedAgent.userPrincipalName);
    } else {
      targetAgent = this._ms365Groups.find((agent) => agent.name === selectedAgent.name);
    }

    const agent = this._createAgent(targetAgent);

    if (agent.type === VoiceAppViewEntityType.USER) {
      this._photoContext
        .initCallQueueUserPhotos(this.callQueueViewSource().tenantId.toString(), [agent.upn])
        .subscribe();
      this._rowUsers.push({ ...agent, photoBase64: '' });
      this._userGuids = [...this._userGuids, agent.guid];
    } else {
      this._viewGroups = [...this._viewGroups, agent];
      this._groupGuids = [...this._groupGuids, agent.guid];
    }

    this._emitAgentSelectionForActiveMode();
    this._buildTableRows();
    this.agentPopover().hide();

    this._agentPhotoSub = this._photoContext.state$.pipe(
      tap(() => {
        const userPhotos: UserPhoto[] = this._photoContext.getUserPhotos();
        this._rowUsers = this._rowUsers.map((user) => ({
          ...user,
          photoBase64: userPhotos.find((photo) => photo.userPrincipalName === user.upn)?.photoBase64 || '',
        }));
        this._buildTableRows();
      }),
    );
    this._subs.add(this._agentPhotoSub.subscribe());
  }

  onAgentPopoverOpen(): void {
    document.querySelector<HTMLInputElement>('[data-automation="agent-type-ahead"]').focus();
  }

  onPopoverClose(inputEl: HTMLInputElement): void {
    inputEl.value = '';
  }

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  isChannelConfigurationUnresolved(): boolean {
    return this.activeAgentMode === AgentMode.TEAMS_CHANNEL && this._isLoadedChannelConfigurationUnresolved;
  }

  isAgentsValid(): boolean {
    if (this.activeAgentMode !== AgentMode.USERS_AND_GROUPS) {
      return true;
    }

    return (
      this._rowUsers.every((user) =>
        user.status === MicrosoftTeamsCallQueueViewEntityStatus.VALID
          ? true
          : !this._userGuids.find((guid) => guid === user.guid),
      ) &&
      this._viewGroups.every((group) =>
        group.status === MicrosoftTeamsCallQueueViewEntityStatus.VALID
          ? true
          : !this._groupGuids.find((guid) => guid === group.guid),
      )
    );
  }

  removeInvalidAgents(): void {
    const invalidUserGuids = this._rowUsers
      .filter((user) => user.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND)
      .map((user) => user.guid);
    const invalidGroupGuids = this._viewGroups
      .filter((group) => group.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND)
      .map((group) => group.guid);

    this._userGuids = this._userGuids.filter((guid) => !invalidUserGuids.includes(guid));
    this._groupGuids = this._groupGuids.filter((guid) => !invalidGroupGuids.includes(guid));

    this._emitAgentSelectionForActiveMode();
    this._buildTableRows();
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _withKnownOption(
    options: ZiroSelectOption[],
    knownOption: ZiroSelectOption,
    currentValue: string,
  ): ZiroSelectOption[] {
    if (!knownOption || knownOption.value !== currentValue || options.some((option) => option.value === currentValue)) {
      return options;
    }

    return [
      ...options,
      {
        ...knownOption,
        disabled: true,
        disabledTooltip: this._translateService.instant('tkey;teams_call_queue.agents.option.not_found.tooltip'),
      },
    ];
  }

  private _emitAgentSelectionForActiveMode(): void {
    const { teamId, teamSchedulingGroupId, microsoftTeamsChannelId } = this.formData;
    const clearedSelection: UiAgentSelection = {
      mode: this.activeAgentMode,
      userGuids: [],
      groupGuids: [],
      microsoftTeamsTeamsId: '',
      teamSchedulingGroupId: '',
      microsoftTeamsChannelId: '',
    };
    let selection: UiAgentSelection;

    if (this.activeAgentMode === AgentMode.TEAMS_CHANNEL) {
      selection = {
        ...clearedSelection,
        groupGuids: teamId ? [teamId] : [],
        microsoftTeamsChannelId,
      };
    } else if (this.activeAgentMode === AgentMode.SHIFTS) {
      selection = {
        ...clearedSelection,
        microsoftTeamsTeamsId: teamId,
        teamSchedulingGroupId,
      };
    } else {
      selection = {
        ...clearedSelection,
        userGuids: this._userGuids,
        groupGuids: this._groupGuids,
      };
    }

    this.agentSelectionChanged.emit(selection);
  }

  private _loadTeamOptions(): void {
    if (this._isTeamOptionsRequested) {
      return;
    }
    this._isTeamOptionsRequested = true;
    this._isLoadingTeamOptions = true;

    const teamsSub = this._teamsResource
      .get(this.callQueueViewSource().tenantId.toString())
      .subscribe((fieldConfig) => {
        this._isLoadingTeamOptions = false;
        const options: ZiroSelectOption[] = fieldConfig.possibleOptions.map((team) => ({
          label: team.displayName,
          value: team.id,
        }));

        this.fieldChannels?.teamId?.componentConfigSource.next(
          new ZiroSelectConfig({
            options: this._withKnownOption(options, this._knownTeamOption, this._selectedTeamId),
            placeholder: 'tkey;teams_call_queue.agents.team.placeholder',
            bindValue: 'value',
          }),
        );
      });

    this._subs.add(teamsSub);
  }

  private _onSelectedTeamChanged(): void {
    this._isLoadedChannelConfigurationUnresolved = false;
    this.fieldChannels?.microsoftTeamsChannelId?.entitySource.next('');
    this.fieldChannels?.teamSchedulingGroupId?.entitySource.next('');
    this._dependentOptionsResetRequests.next();
    this._loadDependentOptions();
  }

  private _loadDependentOptions(): void {
    if (!this._selectedTeamId) {
      return;
    }

    if (this.activeAgentMode === AgentMode.TEAMS_CHANNEL) {
      this._isLoadingChannelOptions = true;
      this._channelOptionsTeamRequests.next(this._selectedTeamId);
    } else if (this.activeAgentMode === AgentMode.SHIFTS) {
      this._isLoadingSchedulingGroupOptions = true;
      this._schedulingGroupOptionsTeamRequests.next(this._selectedTeamId);
    }
  }

  private _createAgent(agent: MicrosoftTeamsUserResult | Microsoft365GroupSearchResult): VoiceAppViewEntity {
    if ('userPrincipalName' in agent) {
      const {
        userPrincipalName: upn,
        lineUri,
        lineUriExtension,
        ref: { displayName },
        guid,
      } = agent;

      return {
        type: VoiceAppViewEntityType.USER,
        description: '',
        upn,
        guid: guid,
        displayName,
        lineUri,
        lineUriExtension,
        status: MicrosoftTeamsCallQueueViewEntityStatus.VALID,
      };
    } else {
      const { id, description, name: displayName } = agent;
      return {
        type: VoiceAppViewEntityType.GROUP,
        lineUri: '',
        lineUriExtension: '',
        upn: '',
        guid: id,
        description,
        displayName,
        status: MicrosoftTeamsCallQueueViewEntityStatus.VALID,
      };
    }
  }

  private _buildTableGraphicMarkup(agent: VoiceAppViewEntity | UiCallQueueUser): string {
    if ('photoBase64' in agent) {
      if (agent.photoBase64) {
        return `
          <span class="d-flex align-items-center">
            <span class="datatable-row-graphic me-2">
              <img src="data:image/png;base64,${agent.photoBase64}" alt="${agent.displayName}">
            </span>
            <span>${agent.displayName}</span>
          </span>
        `;
      } else {
        return `
            <span class="d-flex align-items-center left-3">
                <i class="${ZiroIcons.CIRCLE_USER} font-size-30 me-1"></i>
              <span>${agent.displayName}</span>
            </span>
          `;
      }
    } else {
      return `
          <div>
            <span class="fa-stack left-3">
              <i class="${ZiroIcons.CIRCLE} fa-stack-2x"></i>
              <i class="${ZiroIcons.GROUP} text-white fa-stack-1x"></i>
            </span>
            <span>${agent.displayName}</span>
          </div>
        `;
    }
  }

  private _buildTableRows(): void {
    const deleteAction: DatatableRowActionButton<AgentDatatableRow> = {
      dataAutomation: 'delete-agent',
      style: ButtonStyles.OUTLINE_DANGER,
      type: ButtonTypes.BUTTON,
      icon: ZiroIcons.DELETE_SMALL,
      onClick: (row: AgentDatatableRow) => {
        if (row.type === TeamsCallQueueType.USER) {
          this._rowUsers = this._rowUsers.filter((rowUser) => rowUser.guid !== row.guid);
          this._userGuids = this._userGuids.filter((guid) => guid !== row.guid);
        } else {
          this._viewGroups = this._viewGroups.filter((group) => group.guid !== row.guid);
          this._groupGuids = this._groupGuids.filter((guid) => guid !== row.guid);
        }

        this._emitAgentSelectionForActiveMode();
        this._buildTableRows();
      },
    };

    this.agentRows.set(
      [...this._viewGroups, ...this._rowUsers].map((agent): AgentDatatableRow => {
        const invalidAgent = agent.status === MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND;
        if (agent.type === VoiceAppViewEntityType.USER) {
          return {
            type: TeamsCallQueueType.USER,
            name: agent.displayName,
            upn: agent.upn,
            guid: agent.guid,
            actions: [deleteAction],
            html: {
              type: invalidAgent
                ? `<i class="${ZiroIcons.WARNING_TRIANGLE} text-warning me-1"></i><span>${TeamsCallQueueType.USER}</span>`
                : '',
              name: this._buildTableGraphicMarkup(agent),
            },
            hardcodedTooltips: {
              type: this._translateService.instant('tkey;teams_call_queue.invalid.tooltip', {
                type: TeamsCallQueueType.USER,
              }),
            },
            hardcodedDisableTooltip: {
              type: !invalidAgent,
            },
            cssClass: invalidAgent ? 'table-warning' : '',
          };
        } else {
          return {
            type: TeamsCallQueueType.GROUP,
            name: agent.displayName,
            upn: '',
            guid: agent.guid,
            actions: [deleteAction],
            html: {
              type: invalidAgent
                ? `<i class="${ZiroIcons.WARNING_TRIANGLE} text-warning me-1"></i><span>${TeamsCallQueueType.GROUP}</span>`
                : '',
              name: this._buildTableGraphicMarkup(agent),
            },
            hardcodedTooltips: {
              type: this._translateService.instant('tkey;teams_call_queue.invalid.tooltip', {
                type: TeamsCallQueueType.GROUP,
              }),
            },
            hardcodedDisableTooltip: {
              type: !invalidAgent,
            },
            cssClass: invalidAgent ? 'table-warning' : '',
          };
        }
      }),
    );
  }
}
